/**
 * Error types of the bridge. Every error thrown by the Workspace API client or the Open in Nanome pipeline is a
 * BridgeError with the `step` it failed in; the pipeline adds `partial` once a workspace exists so the UI can offer
 * "Delete partial workspace" (cleanupPartial) or "Open anyway".
 */

/** Names of the pipeline steps, in order (open-in-nanome.ts). The client uses free-form step names too. */
export type PipelineStep =
  | 'info' | 'session' | 'preflight' | 'prepare' | 'workspace' | 'load' | 'scene' | 'components' | 'hidden' | 'frames' | 'public' | 'done';

export interface PartialWorkspace { workspaceId: string; url: string }

/** How much of a server response body is kept on an error (OIN/pipeline.js:41 MAX_BODY). */
export const MAX_ERROR_BODY = 20000;

export class BridgeError extends Error {
  partial?: PartialWorkspace;
  constructor(public readonly step: string, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'BridgeError';
  }
}

/**
 * A Workspace API (or accounts API) request that failed. `status` 0 means no response (network error, timeout or
 * abort; see `code`). `code` carries the server's `{message, code}` code when it sent one: 'LICENSE_ERROR' on a 400
 * for license limits (WorkspaceAPI RestApi.cs:195-228).
 */
export class ApiError extends BridgeError {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly body: string;
  readonly code: string | undefined;
  readonly url: string;
  readonly method: string;
  readonly serverMessage: string | undefined;

  constructor(step: string, message: string, info: { status: number; body?: string; code?: string | undefined; url: string; method: string; serverMessage?: string | undefined; cause?: unknown }) {
    super(step, `${step}: ${message}`, { cause: info.cause });
    this.status = info.status;
    this.body = (info.body ?? '').slice(0, MAX_ERROR_BODY);
    this.code = info.code;
    this.url = info.url;
    this.method = info.method;
    this.serverMessage = info.serverMessage;
  }

  get isLicenseError(): boolean { return this.code === 'LICENSE_ERROR'; }
  get isAborted(): boolean { return this.code === 'ABORTED'; }
  get isTimeout(): boolean { return this.code === 'TIMEOUT'; }

  toJSON() {
    const { name, step, message, status, body, code, url, method, partial } = this;
    return { name, step, message, status, body, code, url, method, partial };
  }
}

/** HTTP 401: the token is gone or expired. There is no refresh grant; the UI reruns the OAuth flow and calls again. */
export class AuthExpired extends BridgeError {
  override readonly name = 'AuthExpired';
  constructor(step: string, public readonly url: string) {
    super(step, `${step}: the Nanome session is no longer valid (HTTP 401); sign in again`);
  }
}

export type PreflightReason =
  /** Free license already owns the maximum number of workspaces (License.cs:13, 3). */
  | 'license-workspaces'
  /** More entries than a free license allows per workspace (License.cs:14, 5). */
  | 'license-entries'
  /** An entry's format cannot be loaded by the server and could not be converted here (.bcif without an RCSB id, unknown extension). */
  | 'unsupported-format'
  /** Nothing to send. */
  | 'no-entries'
  /** A model is missing for an entry that needs conversion or free-form filters. */
  | 'missing-model';

export interface PreflightDetails {
  reason: PreflightReason;
  licenseType?: string;
  ownedWorkspaces?: number;
  maxOwnedWorkspaces?: number;
  entryCount?: number;
  maxEntries?: number;
  /** Entries affected (serial and file name), for pickers and messages. */
  entries?: { serial: number; name: string; fileName: string; note?: string }[];
  /** Lite-created workspace ids remembered in localStorage, so the UI can offer "delete your oldest Lite workspace". */
  liteWorkspaceIds?: string[];
}

/** Thrown before anything is created on the server. */
export class PreflightError extends BridgeError {
  override readonly name = 'PreflightError';
  constructor(message: string, public readonly details: PreflightDetails) {
    super('preflight', message);
  }
}

export function isAbortError(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { name?: string }).name === 'AbortError';
}
